import type { IMainGenre, ISelectOptionWithGroup } from '@intelligence/types/common';
import type { MainGenre, SubGenre } from '@intelligence/components/GenreSelectMiddleware/types';

/**
 * 扩展的 SubGenre 类型，包含 indeterminate 字段
 */
interface SubGenreWithIndeterminate extends SubGenre {
  indeterminate?: boolean;
}

/**
 * 品类筛选器 genretor 的 value 类型
 */
interface IGenretorValue {
  source: string;
  mainGenres: MainGenre[];
  subGenres: SubGenreWithIndeterminate[];
  level3Genres: SubGenreWithIndeterminate[];
  level4Genres: SubGenreWithIndeterminate[];
  level5Genres: SubGenre[];
}

/**
 * 从扁平数组中获取指定一级品类下所有二级品类的集合（去重）
 */
const getAllSubGenresForMain = (options: IMainGenre[], superTag: string): Set<string> => {
  const subGenres = new Set<string>();
  for (const record of options) {
    if (record.main_genre?.genre === superTag) {
      const subGenre = record.sub_genre?.[0]?.genre;
      if (subGenre) {
        subGenres.add(subGenre);
      }
    }
  }
  return subGenres;
};

/**
 * 从扁平数组中获取指定二级品类下所有三级品类的集合（去重）
 */
const getAllLevel3GenresForSub = (options: IMainGenre[], superTag: string, majorTag: string): Set<string> => {
  const level3Genres = new Set<string>();
  for (const record of options) {
    if (record.main_genre?.genre === superTag && record.sub_genre?.[0]?.genre === majorTag) {
      const level3Genre = record.level_3_genre?.[0]?.genre;
      if (level3Genre) {
        level3Genres.add(level3Genre);
      }
    }
  }
  return level3Genres;
};

/**
 * 从扁平数组中获取指定三级品类下所有四级品类的集合（去重）
 */
const getAllLevel4GenresForLevel3 = (
  options: IMainGenre[],
  superTag: string,
  majorTag: string,
  level3Tag: string,
): Set<string> => {
  const level4Genres = new Set<string>();
  for (const record of options) {
    if (
      record.main_genre?.genre === superTag &&
      record.sub_genre?.[0]?.genre === majorTag &&
      record.level_3_genre?.[0]?.genre === level3Tag
    ) {
      const level4Genre = record.level_4_genre?.[0]?.genre;
      if (level4Genre) {
        level4Genres.add(level4Genre);
      }
    }
  }
  return level4Genres;
};

/**
 * 从扁平数组中获取指定四级品类下所有五级品类的集合（去重）
 */
const getAllLevel5GenresForLevel4 = (
  options: IMainGenre[],
  superTag: string,
  majorTag: string,
  level3Tag: string,
  level4Tag: string,
): Set<string> => {
  const level5Genres = new Set<string>();
  for (const record of options) {
    if (
      record.main_genre?.genre === superTag &&
      record.sub_genre?.[0]?.genre === majorTag &&
      record.level_3_genre?.[0]?.genre === level3Tag &&
      record.level_4_genre?.[0]?.genre === level4Tag
    ) {
      const level5Genre = record.level_5_genre?.[0]?.genre;
      if (level5Genre) {
        level5Genres.add(level5Genre);
      }
    }
  }
  return level5Genres;
};

/**
 * 计算一级品类的 checked 和 indeterminate 状态
 */
const calculateMainGenreStatus = (
  options: IMainGenre[],
  selectedSubGenres: SubGenreWithIndeterminate[],
  mainValue: string,
): { checked: boolean; indeterminate: boolean } => {
  const allSubGenres = getAllSubGenresForMain(options, mainValue);
  const allSubGenresCount = allSubGenres.size;
  const selectedCount = selectedSubGenres.filter((sub) => sub.parentValue === mainValue).length;

  if (selectedCount === 0) {
    return { checked: false, indeterminate: false };
  }
  if (selectedCount === allSubGenresCount) {
    return { checked: true, indeterminate: false };
  }
  return { checked: false, indeterminate: true };
};

/**
 * 计算二级品类的 checked 和 indeterminate 状态
 */
const calculateSubGenreStatus = (
  options: IMainGenre[],
  selectedLevel3Genres: SubGenreWithIndeterminate[],
  mainValue: string,
  subValue: string,
): { checked: boolean; indeterminate: boolean } => {
  const allLevel3Genres = getAllLevel3GenresForSub(options, mainValue, subValue);
  const allLevel3GenresCount = allLevel3Genres.size;
  const selectedCount = selectedLevel3Genres.filter(
    (l3) => l3.parentValue === mainValue && l3.parent2Value === subValue,
  ).length;

  if (selectedCount === 0) {
    return { checked: false, indeterminate: false };
  }
  if (selectedCount === allLevel3GenresCount) {
    return { checked: true, indeterminate: false };
  }
  return { checked: false, indeterminate: true };
};

/**
 * 计算三级品类的 checked 和 indeterminate 状态
 */
const calculateLevel3GenreStatus = (
  options: IMainGenre[],
  selectedLevel4Genres: SubGenre[],
  mainValue: string,
  subValue: string,
  level3Value: string,
): { checked: boolean; indeterminate: boolean } => {
  const allLevel4Genres = getAllLevel4GenresForLevel3(options, mainValue, subValue, level3Value);
  const allLevel4GenresCount = allLevel4Genres.size;
  const selectedCount = selectedLevel4Genres.filter(
    (l4) => l4.parentValue === mainValue && l4.parent2Value === subValue && l4.parent3Value === level3Value,
  ).length;

  // 如果没有四级品类，则三级品类为全选状态
  if (allLevel4GenresCount === 0) {
    return { checked: true, indeterminate: false };
  }
  if (selectedCount === 0) {
    return { checked: false, indeterminate: false };
  }
  if (selectedCount === allLevel4GenresCount) {
    return { checked: true, indeterminate: false };
  }
  return { checked: false, indeterminate: true };
};

/**
 * 计算四级品类的 checked 和 indeterminate 状态
 */
const calculateLevel4GenreStatus = (
  options: IMainGenre[],
  selectedLevel5Genres: SubGenre[],
  mainValue: string,
  subValue: string,
  level3Value: string,
  level4Value: string,
): { checked: boolean; indeterminate: boolean } => {
  const allLevel5Genres = getAllLevel5GenresForLevel4(options, mainValue, subValue, level3Value, level4Value);
  const allLevel5GenresCount = allLevel5Genres.size;
  const selectedCount = selectedLevel5Genres.filter(
    (l5) =>
      l5.parentValue === mainValue &&
      l5.parent2Value === subValue &&
      l5.parent3Value === level3Value &&
      l5.parent4Value === level4Value,
  ).length;

  // 如果没有五级品类，则四级品类为全选状态
  if (allLevel5GenresCount === 0) {
    return { checked: true, indeterminate: false };
  }
  if (selectedCount === 0) {
    return { checked: false, indeterminate: false };
  }
  if (selectedCount === allLevel5GenresCount) {
    return { checked: true, indeterminate: false };
  }
  return { checked: false, indeterminate: true };
};

/**
 * 合并 App Magic 品类筛选器的 value（追加模式）
 *
 * 算法说明：
 * 1. 使用 Map 结构去重合并各层级品类（一级、二级、三级、四级）
 * 2. 从最底层（level4）开始向上重新计算 checked/indeterminate 状态
 * 3. 保证合并后的数据状态一致性
 *
 * 合并步骤：
 * - 先合并 mainGenres（一级品类），使用 value 作为唯一键
 * - 再合并 subGenres（二级品类），使用 parentValue + value 作为唯一键
 * - 然后合并 level3Genres（三级品类），使用完整路径作为唯一键
 * - 最后合并 level4Genres（四级品类），使用完整路径作为唯一键
 * - 从 level4 开始向上重新计算各层级的 checked/indeterminate 状态
 *
 * @param currentValue - 当前品类筛选器的值
 * @param newValue - 新构建的品类值
 * @param appMagicOptions - App Magic 品类 options 数据（扁平数组）
 * @returns 合并后的品类筛选器值
 */
export const mergeAppMagicGenreValue = (
  currentValue: IGenretorValue | undefined,
  newValue: IGenretorValue,
  appMagicOptions: IMainGenre[],
): IGenretorValue => {
  if (!currentValue || currentValue.source !== 'appmagic') {
    // 如果当前没有值或不是 appmagic 数据源，直接返回新值
    return newValue;
  }

  // 合并 mainGenres
  const mergedMainGenresMap = new Map<string, MainGenre>();
  for (const main of currentValue.mainGenres) {
    mergedMainGenresMap.set(main.value, { ...main });
  }
  for (const main of newValue.mainGenres) {
    if (!mergedMainGenresMap.has(main.value)) {
      mergedMainGenresMap.set(main.value, { ...main });
    }
  }

  // 合并 subGenres（使用 parentValue + value 作为唯一键）
  const mergedSubGenresMap = new Map<string, SubGenreWithIndeterminate>();
  for (const sub of currentValue.subGenres) {
    const key = `${sub.parentValue}__${sub.value}`;
    mergedSubGenresMap.set(key, { ...sub });
  }
  for (const sub of newValue.subGenres) {
    const key = `${sub.parentValue}__${sub.value}`;
    if (!mergedSubGenresMap.has(key)) {
      mergedSubGenresMap.set(key, { ...sub });
    }
  }

  // 合并 level3Genres（使用完整路径作为唯一键）
  const mergedLevel3GenresMap = new Map<string, SubGenreWithIndeterminate>();
  for (const l3 of currentValue.level3Genres) {
    const key = `${l3.parentValue}__${l3.parent2Value}__${l3.value}`;
    mergedLevel3GenresMap.set(key, { ...l3 });
  }
  for (const l3 of newValue.level3Genres) {
    const key = `${l3.parentValue}__${l3.parent2Value}__${l3.value}`;
    if (!mergedLevel3GenresMap.has(key)) {
      mergedLevel3GenresMap.set(key, { ...l3 });
    }
  }

  // 合并 level4Genres（使用完整路径作为唯一键）
  const mergedLevel4GenresMap = new Map<string, SubGenreWithIndeterminate>();
  for (const l4 of currentValue.level4Genres || []) {
    const key = `${l4.parentValue}__${l4.parent2Value}__${l4.parent3Value}__${l4.value}`;
    mergedLevel4GenresMap.set(key, { ...l4 });
  }
  for (const l4 of newValue.level4Genres || []) {
    const key = `${l4.parentValue}__${l4.parent2Value}__${l4.parent3Value}__${l4.value}`;
    if (!mergedLevel4GenresMap.has(key)) {
      mergedLevel4GenresMap.set(key, { ...l4 });
    }
  }

  // 合并 level5Genres（使用完整路径作为唯一键）
  const mergedLevel5GenresMap = new Map<string, SubGenre>();
  for (const l5 of currentValue.level5Genres || []) {
    const key = `${l5.parentValue}__${l5.parent2Value}__${l5.parent3Value}__${l5.parent4Value}__${l5.value}`;
    mergedLevel5GenresMap.set(key, { ...l5 });
  }
  for (const l5 of newValue.level5Genres || []) {
    const key = `${l5.parentValue}__${l5.parent2Value}__${l5.parent3Value}__${l5.parent4Value}__${l5.value}`;
    if (!mergedLevel5GenresMap.has(key)) {
      mergedLevel5GenresMap.set(key, { ...l5 });
    }
  }

  const mergedLevel5Genres = Array.from(mergedLevel5GenresMap.values());

  // 为四级品类重新计算 checked/indeterminate 状态
  const mergedLevel4Genres: SubGenreWithIndeterminate[] = [];
  for (const [, l4Item] of mergedLevel4GenresMap) {
    const { checked, indeterminate } = calculateLevel4GenreStatus(
      appMagicOptions,
      mergedLevel5Genres,
      l4Item.parentValue,
      l4Item.parent2Value!,
      l4Item.parent3Value!,
      l4Item.value,
    );
    mergedLevel4Genres.push({ ...l4Item, checked, indeterminate });
  }

  // 为三级品类重新计算 checked/indeterminate 状态
  const mergedLevel3Genres: SubGenreWithIndeterminate[] = [];
  for (const [, l3Item] of mergedLevel3GenresMap) {
    const { checked, indeterminate } = calculateLevel3GenreStatus(
      appMagicOptions,
      mergedLevel4Genres,
      l3Item.parentValue,
      l3Item.parent2Value!,
      l3Item.value,
    );
    mergedLevel3Genres.push({ ...l3Item, checked, indeterminate });
  }

  // 为二级品类重新计算 checked/indeterminate 状态
  const mergedSubGenres: SubGenreWithIndeterminate[] = [];
  for (const [, subItem] of mergedSubGenresMap) {
    const { checked, indeterminate } = calculateSubGenreStatus(
      appMagicOptions,
      mergedLevel3Genres,
      subItem.parentValue,
      subItem.value,
    );
    mergedSubGenres.push({ ...subItem, checked, indeterminate });
  }

  // 重新计算每个一级品类的 checked/indeterminate 状态
  const mergedMainGenres: MainGenre[] = [];
  for (const [mainValue, mainItem] of mergedMainGenresMap) {
    const { checked, indeterminate } = calculateMainGenreStatus(appMagicOptions, mergedSubGenres, mainValue);
    mergedMainGenres.push({
      ...mainItem,
      checked,
      indeterminate,
    });
  }

  return {
    source: 'appmagic',
    mainGenres: mergedMainGenres,
    subGenres: mergedSubGenres,
    level3Genres: mergedLevel3Genres,
    level4Genres: mergedLevel4Genres,
    level5Genres: mergedLevel5Genres,
  };
};

// AU异动指标映射
const auSurgeMetricMap: IRecord<ISelectOptionWithGroup> = {
  daily: { value: 'dau', label: 'dbi_surge_metric_dau', text: 'dbi_surge_metric_dau' },
  weekly: { value: 'wau', label: 'dbi_surge_metric_wau', text: 'dbi_surge_metric_wau' },
  monthly: { value: 'mau', label: 'dbi_surge_metric_mau', text: 'dbi_surge_metric_mau' },
};

/**
 * 获取异动指标options
 * @param granularity - 日期粒度
 * @return 异动指标options
 */
export const getSurgeMetricOptions = (granularity: IGranularity): ISelectOptionWithGroup[] => {
  const auSurgeMetric = auSurgeMetricMap[granularity] || auSurgeMetricMap.daily;

  return [
    auSurgeMetric,
    { value: 'revenue', label: 'dbi_surge_metric_revenue', text: 'dbi_surge_metric_revenue' },
    { value: 'download', label: 'dbi_surge_metric_download', text: 'dbi_surge_metric_download' },
  ];
};
